import React, { useCallback, useState } from "react";
import { Button, Drawer } from "antd";
import { LocationType, SecurePassAutoSend } from "@/utils/enum";
import { getAddressByEntity } from "@/services/addresses";
import useScreenDetector from "@/hooks/useScreenDetector";
import AddressBookItemList from "@/components/AddressBookManager/components/AddressBookItemList";
import BookManagerHeader from "@/components/AddressBookManager/components/BookManagerHeader";
import DrawerWrapper from "@/components/DrawerWrapper";
import { parsePhoneNumber } from "@/components/PhoneNumberInput";

const AddressBookManager = ({ formRef, locationType, disabled, onSelectAddress }) => {
  const { isMobile } = useScreenDetector();

  const [visible, setVisible] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [fetchingLatestAddresses, setFetchingLatestAddresses] = useState(false);

  const openDrawer = useCallback(() => {
    setVisible(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setVisible(false);
  }, []);

  const autoFillAddressFields = useCallback(
    /**
     * @param {Address} addressData
     */
    addressData => {
      const {
        id,
        name: locationName,
        addressLine1,
        addressLine2,
        city,
        stateId,
        stateName,
        stateShortName,
        zipCode,
        contactEmail,
        contactName,
        contactPhone,
        contactPhoneExtension,
        dayOfWeekRestricted,
        pickupNotes,
        dropOffNotes,
        autosendType,
        latitude,
        longitude,
      } = addressData;

      // when autosendType is not set we get 0 from BE, so we need to set the default value
      const autosendTypeValue = autosendType === 0 ? SecurePassAutoSend.DEFAULT : autosendType;

      // callback copy of the selected address
      onSelectAddress?.(addressData);

      formRef.current.setFieldsValue({
        [locationType]: {
          originalAddressId: id,
          locationName,
          addressLine1,
          addressLine2,
          city,
          stateId,
          stateName,
          stateShortName,
          zipCode,
          contactEmail,
          contactName,
          contactPhone: parsePhoneNumber(contactPhone),
          contactPhoneExtension,
          instructionsOrRestrictions: locationType === LocationType.Pickup ? pickupNotes : dropOffNotes,
          dayOfWeekRestricted: dayOfWeekRestricted?.split(",") ?? [],
          ...(locationType === LocationType.Pickup && {
            autosendType: autosendTypeValue,
          }),
          latitude,
          longitude,
        },
      });

      closeDrawer();
    },
    [closeDrawer, formRef, locationType, onSelectAddress],
  );

  const fetchDealerAddresses = useCallback(async () => {
    if (fetchingLatestAddresses) {
      return;
    }

    setFetchingLatestAddresses(true);

    try {
      const selectedDealerId = formRef?.current?.getFieldValue("dealerId");
      const res = await getAddressByEntity(selectedDealerId);
      setAddresses(res?.items || []);
    } catch (err) {
      console.error(err);
    } finally {
      setFetchingLatestAddresses(false);
    }
  }, [fetchingLatestAddresses, formRef]);

  const onDrawerOpenOrClose = useCallback(
    isOpened => {
      if (isOpened) {
        fetchDealerAddresses();
      }
    },
    [fetchDealerAddresses],
  );

  const onClearAddress = useCallback(() => {
    formRef?.current?.setFieldsValue({
      [locationType]: {
        originalAddressId: null,
        locationName: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        stateId: "",
        stateName: "",
        stateShortName: null,
        zipCode: "",
        contactEmail: "",
        contactName: "",
        contactPhone: "",
        contactPhoneExtension: "",
        instructionsOrRestrictions: "",
        autosendType: SecurePassAutoSend.DEFAULT,
        saveAddress: false,
        dayOfWeekRestricted: [],
        latitude: null,
        longitude: null,
      },
    });
  }, [locationType, formRef]);

  return (
    <div>
      <section
        style={{
          display: "flex",
          gap: 8,
        }}
      >
        <Button type="primary" disabled={disabled} onClick={openDrawer} size="small">
          Manage Addresses
        </Button>
        <Button type="secondary" onClick={onClearAddress} size="small">
          Clear
        </Button>
      </section>
      <DrawerWrapper visible={visible}>
        <Drawer
          title={<BookManagerHeader addresses={addresses} setAddresses={setAddresses} />}
          width={isMobile ? "100%" : "40%"}
          onClose={closeDrawer}
          open={visible}
          afterOpenChange={onDrawerOpenOrClose}
          bodyStyle={{
            position: "relative",
          }}
        >
          <AddressBookItemList
            addresses={addresses}
            fetchingLatestAddresses={fetchingLatestAddresses}
            onEditSuccess={fetchDealerAddresses}
            autoFillAddressFields={autoFillAddressFields}
          />
        </Drawer>
      </DrawerWrapper>
    </div>
  );
};

export default AddressBookManager;
